/**
 * CheckoutDrawer — centered modal for creating OR editing a kiosk /
 * checkout station. (File name kept for backward compat with existing
 * imports; UX is now a centered modal, not a slide-in drawer.)
 *
 * Layout matches the latest Figma for "Add checkout":
 *   Title + subtitle
 *   • Checkout name*  (text input)
 *   • Site*           (dropdown)
 *   • Template*       (dropdown, auto-selects the org default template)
 *   • Read-only preview card showing the template's Authentication method,
 *     Logo and Color scheme, with an external-link icon to open the
 *     template's full settings in a new browser tab.
 *   • (Edit only) Status dropdown
 *   • Cancel / Save footer
 *
 * The latest Figma removes the separate Location field from the modal.
 * To preserve the backend contract and the list page's LOCATION column,
 * we persist the checkout name into the `location` field on save.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTenant } from '../../context/TenantContext';
import checkoutService from '../../services/checkoutService';
import checkoutTemplateService from '../../services/checkoutTemplateService';

const DEFAULT_FORM = {
  checkoutName: '',
  siteId: '',
  location: '',
  template: '',
  status: 'OFFLINE',
};

const STATUS_OPTIONS = [
  { value: 'ONLINE',  label: 'Online' },
  { value: 'OFFLINE', label: 'Offline' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ERROR',   label: 'Error' },
];

/**
 * Template preview attributes shown in the read-only details card.
 * Keyed by the enum used in TEMPLATE_OPTIONS so changing the template
 * dropdown updates the preview card instantly.
 */
const TEMPLATE_PREVIEW = {
  STANDARD: {
    authentication: 'PIN (4-digit)',
    logo: 'logo.png, logo2.png',
    colorScheme: 'Color scheme 1',
  },
  OPEN_ACCESS: {
    authentication: 'None',
    logo: 'logo.png',
    colorScheme: 'Color scheme 2',
  },
  HIGH_SECURITY: {
    authentication: 'PIN (6-digit) + biometric',
    logo: 'logo.png, logo2.png',
    colorScheme: 'Color scheme 3',
  },
};

function HelpIcon({ tip }) {
  return (
    <span className="co-help" title={tip} aria-label={tip}>
      <i className="far fa-question-circle"></i>
    </span>
  );
}

/**
 * Minimal dark single-select, reuses drawer-mselect palette so the look
 * matches other drawers/modals in the app.
 */
function Select({ placeholder, options, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selectedOpt = options.find((o) => String(o.value) === String(value));

  return (
    <div className={`drawer-mselect ${open ? 'open' : ''}`} ref={rootRef}>
      <div
        role="button"
        tabIndex={0}
        className={`drawer-input drawer-mselect-trigger ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
        }}
      >
        <div className="drawer-mselect-inner">
          {selectedOpt
            ? (
              <span className="drawer-mselect-single co-select-single">
                <span>{selectedOpt.label}</span>
                {selectedOpt.badge && <span className="co-template-badge">{selectedOpt.badge}</span>}
              </span>
            )
            : <span className="drawer-mselect-placeholder">{placeholder}</span>}
        </div>
        <i className="fas fa-chevron-down drawer-mselect-caret"></i>
      </div>
      {open && (
        <div className="drawer-mselect-menu" role="listbox">
          {options.length === 0 && (
            <div className="drawer-mselect-empty">No options available</div>
          )}
          {options.map((o) => {
            const selected = String(o.value) === String(value);
            return (
              <button
                type="button"
                key={o.value}
                className={`drawer-mselect-item ${selected ? 'checked' : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}
              >
                <span className="drawer-mselect-label co-select-option-label">
                  <span>{o.label}</span>
                  {o.badge && <span className="co-template-badge">{o.badge}</span>}
                </span>
                {selected && <i className="fas fa-check drawer-mselect-tick"></i>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Map a backend CheckoutResponse (or mapped row) into the modal form. */
function formFromCheckout(c) {
  if (!c) return DEFAULT_FORM;
  const rawStatus = String(c.statusRaw ?? c.status ?? 'OFFLINE').toUpperCase();
  const statusMap = {
    ACTIVE: 'ONLINE',
    INACTIVE: 'OFFLINE',
    ONLINE: 'ONLINE',
    OFFLINE: 'OFFLINE',
    PENDING: 'PENDING',
    ERROR: 'ERROR',
  };
  const normalizedStatus = statusMap[rawStatus] || 'OFFLINE';
  return {
    checkoutName: c.checkoutName ?? c.name ?? c.location ?? '',
    siteId:       c.siteId ?? '',
    location:     c.location ?? '',
    template:     c.templateRaw ?? c.template ?? '',
    status:       normalizedStatus,
  };
}

/** Build a human-readable preview label for a template's security settings. */
function buildAuthLabel(tmpl) {
  if (!tmpl) return '—';
  if (!tmpl.employeePinRequired) return 'None';
  return `PIN (${tmpl.pinLength ?? 4}-digit)`;
}

/** Build logo display text from a template record. */
function buildLogoLabel(tmpl) {
  if (!tmpl) return '—';
  const parts = [tmpl.logo1Url, tmpl.logo2Url].filter(Boolean);
  if (parts.length === 0) return 'None';
  return parts.map((u) => u.split('/').pop()).join(', ');
}

function resolveSubmitError(err, isEdit, templateCode) {
  const msg =
    err?.response?.data?.message
    ?? err?.response?.data?.error
    ?? err?.message
    ?? '';

  // Legacy DBs still enforcing the old template check constraint surface this
  // as a generic "Invalid value provided" error. Provide an actionable hint.
  if (
    String(msg).toLowerCase().includes('invalid value provided')
    && templateCode
    && !['STANDARD', 'OPEN_ACCESS', 'HIGH_SECURITY'].includes(String(templateCode).toUpperCase())
  ) {
    return 'Custom template assignment failed. Please apply the DB migration that relaxes the checkouts.template constraint (00031_relax_checkouts_template_constraint.sql).';
  }

  return msg || (isEdit ? 'Failed to update checkout' : 'Failed to create checkout');
}

export default function CheckoutDrawer({ open, checkout, onClose, onSaved }) {
  const { orgId, sites } = useTenant();
  const isEdit = !!checkout;

  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Dynamic template list from API
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templateMap, setTemplateMap] = useState({});   // templateCode → template record
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Load templates whenever the drawer opens (or orgId changes).
  useEffect(() => {
    if (!open || !orgId) return;
    let cancelled = false;
    setTemplatesLoading(true);
    checkoutTemplateService
      .listTemplates(orgId, { limit: 200, offset: 0, sortBy: 'templateName', sortOrder: 'asc' })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.content ?? []);
        const opts = list.map((t) => ({
          value: t.templateCode,
          label: t.templateName,
          badge: t.orgDefault ? 'Org default' : '',
          orgDefault: !!t.orgDefault,
        }));
        const map = {};
        list.forEach((t) => { map[t.templateCode] = t; });
        setTemplateOptions(opts);
        setTemplateMap(map);
      })
      .catch(() => {
        if (!cancelled) {
          // Fallback to built-in statics so the drawer still works
          setTemplateOptions([
            { value: 'STANDARD',      label: 'Standard checkout', badge: 'Org default', orgDefault: true },
            { value: 'OPEN_ACCESS',   label: 'Open access checkout' },
            { value: 'HIGH_SECURITY', label: 'High security checkout' },
          ]);
          setTemplateMap({});
        }
      })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [open, orgId]);

  // Reset / prefill when opened, or when the edited row changes.
  // Auto-select the first template for new checkouts once options load.
  useEffect(() => {
    if (open) {
      setForm(isEdit ? formFromCheckout(checkout) : DEFAULT_FORM);
      setError(null);
      setSubmitting(false);
    }
  }, [open, isEdit, checkout]);

  // Auto-select org-default template when creating a new checkout.
  useEffect(() => {
    if (!open || isEdit) return;
    if (templateOptions.length > 0 && !form.template) {
      const orgDefault = templateOptions.find((opt) => opt.orgDefault);
      setForm((prev) => ({ ...prev, template: (orgDefault ?? templateOptions[0]).value }));
    }
  }, [templateOptions, open, isEdit, form.template]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const siteOptions = useMemo(
    () => (sites || []).map((s) => ({
      value: s.siteId ?? s.id,
      label: s.siteName ?? s.name ?? `Site ${s.siteId ?? s.id}`,
    })),
    [sites],
  );

  if (!open) return null;

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const setVal = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));

  const canSubmit =
    form.checkoutName.trim().length > 0 &&
    form.siteId !== '' && form.siteId != null &&
    !!form.template &&
    !!orgId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    const name = form.checkoutName.trim();
    const location = name;
    const statusForApi = String(form.status || 'OFFLINE').toUpperCase();
    const validStatus = ['ONLINE', 'OFFLINE', 'PENDING', 'ERROR'].includes(statusForApi)
      ? statusForApi
      : 'OFFLINE';
    const templateForApi = String(form.template || '').trim();
    try {
      if (isEdit) {
        await checkoutService.updateCheckout(orgId, checkout.id ?? checkout.checkoutId, {
          checkoutName: name,
          siteId:       form.siteId,
          location,
          status:       validStatus,
          template:     templateForApi || undefined,
        });
      } else {
        await checkoutService.createCheckout(orgId, {
          checkoutName: name,
          siteId:       form.siteId,
          location,
          template:     templateForApi || undefined,
        });
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(resolveSubmitError(err, isEdit, templateForApi));
      setSubmitting(false);
    }
  };

  const title    = isEdit ? 'Edit checkout' : 'Add checkout';
  const subtitle = 'Enter checkout details and choose a template.';

  // Live preview derived from the selected template record, with a legacy
  // fallback so the card still renders sensible content if the template API
  // is temporarily unavailable.
  const selectedTemplate = templateMap[form.template];
  const legacyPreview = TEMPLATE_PREVIEW[form.template];
  const preview = {
    authentication: selectedTemplate ? buildAuthLabel(selectedTemplate) : (legacyPreview?.authentication ?? '—'),
    logo:           selectedTemplate ? buildLogoLabel(selectedTemplate) : (legacyPreview?.logo ?? '—'),
    colorScheme:    selectedTemplate?.colorScheme ?? legacyPreview?.colorScheme ?? '—',
  };

  return (
    <div
      className="co-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="co-modal" onClick={(e) => e.stopPropagation()}>
        <header className="co-modal-header">
          <h2 className="co-modal-title">{title}</h2>
          <p className="co-modal-subtitle">{subtitle}</p>
          <button
            type="button"
            className="co-modal-close"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            <i className="fas fa-times"></i>
          </button>
        </header>

        <form className="co-modal-body" onSubmit={handleSubmit} noValidate>
          {/* Checkout name */}
          <div className="co-field">
            <label className="co-label">
              <span className="co-label-text">
                Checkout name<span className="co-req">*</span>
              </span>
                  <HelpIcon tip="The display name for this checkout." />
            </label>
            <input
              type="text"
              className="drawer-input"
              placeholder="Enter name"
              value={form.checkoutName}
              onChange={set('checkoutName')}
              required
            />
          </div>

          {/* Site */}
          <div className="co-field">
            <label className="co-label">
              <span className="co-label-text">
                Site<span className="co-req">*</span>
              </span>
              <HelpIcon tip="The physical location where this checkout is deployed." />
            </label>
            <Select
              placeholder="Select site"
              options={siteOptions}
              value={form.siteId}
              onChange={setVal('siteId')}
            />
          </div>

          <div className="co-section-divider" aria-hidden="true"></div>

          {/* Template — dynamically loaded */}
          <div className="co-field">
            <label className="co-label">
              <span className="co-label-text">
                Template<span className="co-req">*</span>
              </span>
              <HelpIcon tip="Choose a pre-configured template to define the rules and security settings for this checkout." />
            </label>
            <Select
              placeholder={templatesLoading ? 'Loading templates…' : 'Select template'}
              options={templateOptions}
              value={form.template}
              onChange={setVal('template')}
              disabled={templatesLoading}
            />
          </div>

          {/* Read-only template preview card */}
          {form.template && (
            <div className="co-preview" role="group" aria-label="Template preview">
              <button
                type="button"
                className="co-preview-link"
                title="Open template settings"
                aria-label="Open template settings"
                onClick={() => {
                  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
                  const qp = new URLSearchParams();
                  if (selectedTemplate?.templateId != null) qp.set('templateId', String(selectedTemplate.templateId));
                  if (form.template) qp.set('templateCode', String(form.template));
                  window.open(`${base}/checkout/templates?${qp.toString()}`, '_blank', 'noopener,noreferrer');
                }}
              >
                <i className="fas fa-external-link-alt"></i>
              </button>
              <dl className="co-preview-list">
                <div className="co-preview-row">
                  <dt>Authentication method:</dt>
                  <dd>{preview.authentication}</dd>
                </div>
                <div className="co-preview-row">
                  <dt>Logo:</dt>
                  <dd>{preview.logo}</dd>
                </div>
                <div className="co-preview-row">
                  <dt>Color scheme:</dt>
                  <dd>{preview.colorScheme}</dd>
                </div>
              </dl>
            </div>
          )}

          {/* Status — only in edit mode (backend assigns initial state) */}
          {isEdit && (
            <div className="co-field">
              <label className="co-label">
                <span className="co-label-text">Status</span>
              </label>
              <Select
                placeholder="Select status"
                options={STATUS_OPTIONS}
                value={form.status}
                onChange={setVal('status')}
              />
            </div>
          )}

          {error && <div className="drawer-error">{error}</div>}

          <footer className="co-modal-footer">
            <button
              type="button"
              className="btn-ghost-small"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-pill"
              disabled={!canSubmit || submitting}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
